import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Carousel, CarouselContent, CarouselItem } from '../components/ui/carousel';
import Autoplay from "embla-carousel-autoplay";
import { Star, ChevronRight, Truck, Shield, RefreshCcw, Headphones, Tag, Copy } from 'lucide-react';
import { productsAPI, categoriesAPI, bannersAPI, offersAPI } from '../lib/api';
import { fetchWithCache } from '../lib/cache';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../lib/utils';
import SEOHead from '../components/SEOHead';
import { useLoadingToast } from '../hooks/use-loading-toast';

const ProductCard = ({ product }) => {
  const { isWholesale, user } = useAuth();
  const navigate = useNavigate();

  // Determine which price to show based on user role
  let displayPrice = product.selling_price;
  let showWholesaleBadge = false;
  let minQtyText = null;

  if (user?.role === 'pos_user') {
    // POS users see only POS price
    displayPrice = product.pos_price || product.selling_price;
  } else if (isWholesale && product.wholesale_price) {
    // Wholesale users see only wholesale price
    displayPrice = product.wholesale_price;
    showWholesaleBadge = true;
    minQtyText = `Min. ${product.wholesale_min_qty || 10} pcs for wholesale price`;
  }
  // Admin and normal customers see selling price (default)

  const discount = Math.round(((product.mrp - displayPrice) / product.mrp) * 100);

  return (
    <div
      className="card-product group cursor-pointer interactive-card"
      onClick={() => navigate(`/products/${product.id}`)}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-square overflow-hidden rounded-t-2xl">
        <img
          src={getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1624927637280-f033784c1279?w=500'}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />
        {discount > 0 && (
          <Badge className="absolute top-3 left-3 discount-badge">
            {discount}% OFF
          </Badge>
        )}
        {showWholesaleBadge && (
          <Badge className="absolute top-3 right-3 wholesale-badge">
            Wholesale
          </Badge>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors duration-300 mb-2">
          {product.name}
        </h3>
        <div className="flex items-center gap-1 mb-3">
          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          <span className="text-xs text-muted-foreground">4.5 (120)</span>
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-lg font-bold price-tag">
            ₹{displayPrice.toLocaleString()}
          </span>
          <span className="text-sm text-muted-foreground line-through">
            ₹{product.mrp.toLocaleString()}
          </span>
        </div>
        {minQtyText && (
          <p className="text-xs text-primary font-medium">
            {minQtyText}
          </p>
        )}
      </div>
    </div>
  );
};

function HomePage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showLoadingToast, showErrorToast } = useLoadingToast();
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // State for category expansion
  const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const toastIdRef = useRef(null);

  const getBannerAspectRatio = (size) => {
    if (!size || typeof size !== 'string') return '21 / 9';

    const cleaned = size.trim().toLowerCase();
    const byX = cleaned.match(/^(\d+)\s*x\s*(\d+)$/);
    if (byX) {
      const w = parseInt(byX[1], 10);
      const h = parseInt(byX[2], 10);
      if (w > 0 && h > 0) return `${w} / ${h}`;
    }

    const byColon = cleaned.match(/^(\d+)\s*:\s*(\d+)$/);
    if (byColon) {
      const w = parseInt(byColon[1], 10);
      const h = parseInt(byColon[2], 10);
      if (w > 0 && h > 0) return `${w} / ${h}`;
    }

    return '21 / 9';
  };

  const fetchData = useCallback(async () => {
    // Show loading toast for initial data fetch
    const loadingToastId = showLoadingToast('home_data', {
      title: 'Loading products...',
      description: 'Fetching latest products and categories',
    });
    toastIdRef.current = loadingToastId;

    try {
      // Fetch all data with caching - shows cached data instantly, then updates with fresh data
      await Promise.all([
        fetchWithCache(
          'home_products',
          async () => {
            const res = await productsAPI.getTrending(5, 12); // Top sellers in last 5 days
            return Array.isArray(res.data?.products) ? res.data.products : [];
          },
          (data, fromCache) => {
            setProducts(data);
            if (fromCache) setLoading(false); // Show cached data immediately
          }
        ),
        fetchWithCache(
          'home_categories',
          async () => {
            const res = await categoriesAPI.getTree();
            return Array.isArray(res.data) ? res.data : [];
          },
          (data) => {
            setCategories(data);
          }
        ),
        fetchWithCache(
          'home_banners',
          async () => {
            const res = await bannersAPI.getAll();
            return Array.isArray(res.data) ? res.data : [];
          },
          (data) => {
            setBanners(data);
          }
        ),
        fetchWithCache(
          'home_offers',
          async () => {
            const res = await offersAPI.getAll();
            return Array.isArray(res.data) ? res.data : [];
          },
          (data) => {
            setOffers(data);
          }
        ),
      ]);

      // Explicitly dismiss using the ID we have (bypassing potentially stale hook state)
      toast.dismiss(loadingToastId);
    } catch (error) {
      // Explicitly dismiss loading toast on error
      toast.dismiss(loadingToastId);

      // Keep empty arrays
      setProducts([]);
      setCategories([]);
      setBanners([]);
      setOffers([]);

      // Show error toast
      showErrorToast('home_data', {
        title: 'Failed to load data',
        description: error.message || 'Please refresh the page to try again',
      });
    } finally {
      setLoading(false);
    }
  }, [showLoadingToast, showErrorToast]);

  useEffect(() => {
    fetchData();
    try {
      const stored = JSON.parse(localStorage.getItem('pwa_recently_viewed') || '[]');
      if (Array.isArray(stored)) setRecentlyViewed(stored);
    } catch {
      setRecentlyViewed([]);
    }
    return () => {
      if (toastIdRef.current) toast.dismiss(toastIdRef.current);
    };
  }, [fetchData]);

  const features = [
    { icon: Truck, title: 'Free Delivery', desc: 'On orders above ₹1499' },
    { icon: Shield, title: 'Secure Payment', desc: '100% secure checkout' },
    { icon: RefreshCcw, title: 'Easy Returns', desc: '7 days return policy' },
    { icon: Headphones, title: '24/7 Support', desc: 'Dedicated customer care' },
  ];

  const visibleCategories = isCategoriesExpanded ? categories : categories.slice(0, 6);

  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const currentCount = products.length;
      const res = await productsAPI.getAll({
        limit: 30,
        skip: currentCount,
        for_home: true,
        sort_by: 'sales_count',
        sort_order: 'desc'
      });

      const newProducts = Array.isArray(res.data?.products) ? res.data.products : [];
      if (newProducts.length < 30) {
        setHasMore(false);
      }

      setProducts(prev => [...prev, ...newProducts]);
    } catch (error) {
      console.error("Failed to load more products", error);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div data-testid="home-page">
      <SEOHead />
      {/* Hero Banner Carousel */}
      <section className="relative">
        {banners.length > 0 ? (
          <Carousel
            className="w-full"
            plugins={[
              Autoplay({
                delay: 3000,
              }),
            ]}
          >
            <CarouselContent>
              {banners.map((banner, index) => (
                <CarouselItem key={`${banner.id || 'banner'}-${index}`}>
                  <div
                    className="relative w-full min-h-[170px] sm:min-h-[260px] lg:min-h-[320px]"
                    style={{ aspectRatio: getBannerAspectRatio(banner.size) }}
                  >
                    <img
                      src={getImageUrl(banner.image_url)}
                      alt={banner.title}
                      className="w-full h-full object-contain bg-black/10"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end">
                      <div className="w-full px-4 sm:px-6 lg:px-8 pb-6 sm:pb-10">
                        <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white max-w-lg leading-tight text-left">
                          {banner.title}
                        </h1>
                        <Button
                          onClick={() => navigate(banner.link || '/products')}
                          className="mt-6 btn-primary"
                        >
                          Shop Now
                          <ChevronRight className="w-5 h-5 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        ) : (
          <div className="gradient-hero aspect-[21/9] md:aspect-[3/1] flex items-center">
            <div className="max-w-7xl mx-auto px-4 w-full">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white max-w-lg leading-tight">
                Discover Amazing Deals on Fashion & More
              </h1>
              <p className="text-white/85 mt-4 text-base sm:text-lg max-w-md">
                Shop from thousands of products at wholesale prices
              </p>
              <Button
                onClick={() => navigate('/products')}
                className="mt-6 bg-white text-primary hover:bg-white/90 rounded-2xl px-7 py-3 font-semibold shadow-soft"
              >
                Shop Now
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* Offers Section */}
      {
        offers.length > 0 && (
          <section className="py-6 sm:py-8 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-xl sm:text-2xl font-bold mb-3 flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Tag className="w-4 h-4 text-primary" />
                </div>
                <span className="text-primary tracking-tight">Exciting Offers</span>
              </h2>
              <div className={`grid ${offers.filter(o => o.is_active).length > 1 ? 'grid-cols-2' : 'grid-cols-1'} sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3`}>
                {offers.filter(offer => offer.is_active).map((offer, index) => (
                  <div key={offer.id || index} className="glass-card w-full p-3 flex flex-col justify-between border-t-4 border-primary leading-tight overflow-hidden">
                    <div className="flex flex-col gap-2 mb-2">
                      <div className="flex justify-between items-start gap-1">
                        <Badge className="bg-accent text-black text-[10px] px-1.5 py-0.5 rounded shadow-soft flex-shrink-0">
                          {offer.discount_type === 'percentage' ? `${offer.discount_value}% OFF` : `₹${offer.discount_value} OFF`}
                        </Badge>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-sm truncate">{offer.title}</h3>
                        <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{offer.description}</p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 mt-auto pt-2 border-t border-primary/10">
                      {offer.coupon_code && (
                        <div className="bg-primary/5 px-2 py-1 rounded border border-dashed border-primary/30 flex items-center justify-between w-full">
                          <span className="font-mono font-bold text-primary text-[11px] truncate">{offer.coupon_code}</span>
                          <button
                            className="ml-1 text-primary/70 hover:text-primary active:scale-95 transition-transform flex-shrink-0"
                            onClick={() => {
                              navigator.clipboard.writeText(offer.coupon_code);
                              toast.success('Coupon copied!');
                            }}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      <div className="flex justify-between items-center text-[9px] text-muted-foreground font-medium">
                        <span className="truncate">Min: ₹{offer.min_order_value}</span>
                        {offer.end_date && (
                          <span className="truncate ml-1">
                            Till: {new Date(offer.end_date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )
      }

      {/* Features */}
      <section className="py-6 sm:py-8 border-b bg-white/70 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-4 gap-2 sm:gap-6">
            {features.map((feature, index) => (
              <div key={index} className="feature-card group flex flex-col items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center mb-2 sm:mb-3 transition-transform duration-300 group-hover:scale-105 mx-auto">
                  <feature.icon className="w-5 h-5 sm:w-7 sm:h-7 text-primary" />
                </div>
                <div className="text-center">
                  <h4 className="font-semibold text-[10px] sm:text-sm mb-0.5 sm:mb-1 leading-tight">{feature.title}</h4>
                  <p className="text-[9px] sm:text-xs text-muted-foreground hidden lg:block">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="section-padding bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-primary">Shop by Category</h2>
            <div className="flex items-center gap-3">
              {categories.length > 6 && (
                <Button
                  variant="ghost"
                  onClick={() => setIsCategoriesExpanded(!isCategoriesExpanded)}
                  className="text-primary hover:text-primary/80 hover:bg-primary/10 rounded-2xl px-5 py-2 font-semibold transition-all duration-300"
                >
                  {isCategoriesExpanded ? 'Show Less' : 'View All'}
                </Button>
              )}
            </div>
          </div>
          <div className="flex overflow-x-auto md:grid md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6 pb-4 scrollbar-invisible snap-x">
            {visibleCategories.map((category, index) => (
              <Link
                key={`${category.id}-${index}`}
                to={`/products?category=${category.id}`}
                className="group relative aspect-square rounded-2xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-shadow duration-300 w-[130px] sm:w-[160px] md:w-auto flex-shrink-0 snap-start"
                data-testid={`category-${category.id}`}
              >
                <div className="w-full h-full">
                  <img
                    src={getImageUrl(category.image_url) || 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=500'}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white font-semibold text-sm sm:text-base">
                    {category.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trending Products */}
      {recentlyViewed.length > 0 && (
        <section className="pwa-only section-padding">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-primary">Recently Viewed</h2>
              <button
                className="text-xs text-muted-foreground"
                onClick={() => {
                  localStorage.removeItem('pwa_recently_viewed');
                  setRecentlyViewed([]);
                }}
              >
                Clear
              </button>
            </div>
            <div className="flex overflow-x-auto gap-3 pb-4 scrollbar-invisible snap-x">
              {recentlyViewed.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className="card-product p-3 cursor-pointer w-[140px] flex-shrink-0 snap-start"
                  onClick={() => navigate(`/products/${item.id}`)}
                >
                  <div className="aspect-square rounded-xl overflow-hidden mb-2">
                    <img
                      src={getImageUrl(item.image)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="text-sm font-semibold line-clamp-2">{item.name}</div>
                  {item.price && (
                    <div className="text-sm text-primary font-bold mt-1">
                      ₹{item.price.toLocaleString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trending Products */}
      <section className="section-padding bg-muted/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-primary">Trending Products</h2>
            <Link
              to="/products?sort=sales_count-desc"
              className="text-primary font-semibold flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-2 rounded-2xl hover:bg-white"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {loading ? (
            <div className="flex overflow-x-auto md:grid md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6 pb-4 scrollbar-invisible">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="glass-card w-[160px] md:w-auto flex-shrink-0">
                  <div className="aspect-square bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-600 rounded-t-2xl skeleton" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-600 rounded skeleton" />
                    <div className="h-3 w-20 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-600 rounded skeleton" />
                    <div className="h-5 w-24 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-600 rounded skeleton" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex overflow-x-auto md:grid md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6 pb-4 scrollbar-invisible snap-x">
              {products.map((product, index) => (
                <div key={product.id} className="w-[160px] md:w-auto flex-shrink-0 snap-start">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {!loading && hasMore && products.length > 0 && (
            <div className="mt-12 flex justify-center">
              <Button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="btn-secondary min-w-[200px]"
              >
                {loadingMore ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span className="loading-dots">Loading</span>
                  </div>
                ) : (
                  <>
                    Load More Items
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Wholesale Banner */}
      <section className="py-6 sm:py-8 lg:py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary to-[#0b4d4a] opacity-95"></div>
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="glass-heavy rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-white max-w-4xl mx-auto text-center">
            <Badge className="bg-white/20 text-white mb-3 sm:mb-6 px-3 py-1 sm:px-4 sm:py-2 rounded-xl sm:rounded-2xl backdrop-blur-sm text-xs sm:text-sm">
              For Business
            </Badge>
            <h2 className="text-xl sm:text-3xl md:text-4xl font-bold sm:font-extrabold mb-2 sm:mb-4 leading-tight">
              Get Wholesale Prices with GST Registration
            </h2>
            <p className="text-white/90 mb-5 sm:mb-8 text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Register with your GST number and unlock exclusive wholesale prices on bulk orders. Save up to 40% on every purchase!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                onClick={() => navigate('/register')}
                className="bg-white text-primary hover:bg-white/90 rounded-xl sm:rounded-2xl px-6 py-3 sm:px-8 sm:py-4 font-semibold text-sm sm:text-lg shadow-soft"
              >
                Register as Wholesaler
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-xl sm:rounded-2xl px-6 py-3 sm:px-8 sm:py-4 font-semibold text-sm sm:text-lg backdrop-blur-sm"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 sm:py-12 border-t bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-primary">
            Trusted by 10 Lakh+ Customers
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-10 opacity-60">
            {['PhonePe', 'Paytm', 'UPI', 'COD'].map((brand, index) => (
              <div
                key={brand}
                className="text-lg sm:text-2xl font-semibold hover:opacity-100 transition-opacity duration-300"
              >
                {brand}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div >
  );
}

export default HomePage;
